import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { MessageCircle, Settings, Bell, Clock, Send, Play, BarChart3, FileText, Wifi, WifiOff, CheckCircle2, XCircle, MessageSquare, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChatbotConfigPage() {
  const { user, token } = useAuth();
  const { language } = useLanguage();
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [simPhone, setSimPhone] = useState('');
  const [simText, setSimText] = useState('');
  const [simResponse, setSimResponse] = useState('');
  const [simulating, setSimulating] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateEn, setTemplateEn] = useState('');
  const [templateEs, setTemplateEs] = useState('');

  const headers = { Authorization: `Bearer ${token}` };
  const tt = (en, es) => language === 'es' ? es : en;

  const fetchData = async () => {
    try {
      const [cfgRes, statsRes, msgsRes] = await Promise.all([
        axios.get(`${API}/chatbot/config`, { headers }),
        axios.get(`${API}/chatbot/stats`, { headers }),
        axios.get(`${API}/chatbot/messages?limit=30`, { headers }),
      ]);
      setConfig(cfgRes.data);
      setStats(statsRes.data);
      setMessages(msgsRes.data.messages || []);
    } catch { toast.error(tt('Failed to load chatbot config', 'Error al cargar configuracion')); }
    setLoading(false);
  };

  useEffect(() => { if (token && user?.role && ['admin', 'superadmin'].includes(user.role)) fetchData(); }, [token]);

  const updateConfig = async (updates) => {
    try {
      await axios.put(`${API}/chatbot/config`, updates, { headers });
      toast.success(tt('Config updated', 'Configuracion actualizada'));
      fetchData();
    } catch { toast.error(tt('Update failed', 'Error al actualizar')); }
  };

  const saveTemplate = async () => {
    if (!editingTemplate) return;
    try {
      await axios.put(`${API}/chatbot/config/template`, {
        template_key: editingTemplate, en: templateEn, es: templateEs
      }, { headers });
      toast.success(tt('Template saved', 'Plantilla guardada'));
      setEditingTemplate(null);
      fetchData();
    } catch { toast.error(tt('Save failed', 'Error al guardar')); }
  };

  const simulate = async () => {
    if (!simPhone || !simText) return;
    setSimulating(true);
    try {
      const res = await axios.post(`${API}/chatbot/simulate`, { phone: simPhone, text: simText }, { headers });
      setSimResponse(res.data.response);
      fetchData();
    } catch { toast.error(tt('Simulation failed', 'Error en simulacion')); }
    setSimulating(false);
  };

  const triggerReminders = async () => {
    try {
      await axios.post(`${API}/chatbot/trigger-reminders`, {}, { headers });
      toast.success(tt('Reminders sent!', 'Recordatorios enviados!'));
      fetchData();
    } catch { toast.error(tt('Failed', 'Error')); }
  };

  if (loading) return (
    <div className="max-w-5xl mx-auto px-4 py-8 space-y-4">
      <Skeleton className="h-10 w-64" /><Skeleton className="h-48 rounded-xl" />
    </div>
  );

  if (!config) return null;
  const reminders = config.reminders || {};
  const notifications = config.notifications || {};
  const templates = config.templates || {};

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="chatbot-config-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <MessageCircle className="w-7 h-7 text-primary" />
          <div>
            <h1 className="font-heading text-xl md:text-2xl font-semibold">{tt('WhatsApp Chatbot', 'Chatbot WhatsApp')}</h1>
            <p className="text-xs text-slate-500 mt-0.5">{tt('Configure messaging, reminders & notifications', 'Configurar mensajeria, recordatorios y notificaciones')}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {config.whatsapp_connected ? (
            <Badge className="bg-green-100 text-green-700 gap-1"><Wifi className="w-3 h-3" />{tt('Connected', 'Conectado')}</Badge>
          ) : (
            <Badge className="bg-slate-100 text-slate-500 gap-1"><WifiOff className="w-3 h-3" />{tt('Webhook Ready', 'Webhook Listo')}</Badge>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
          {[
            { label: tt('Total', 'Total'), value: stats.total_messages, color: 'text-primary' },
            { label: tt('Incoming', 'Entrantes'), value: stats.incoming, color: 'text-blue-600' },
            { label: tt('Outgoing', 'Salientes'), value: stats.outgoing, color: 'text-green-600' },
            { label: tt('Reminders', 'Recordatorios'), value: stats.reminders_sent, color: 'text-amber-600' },
            { label: tt('Notifications', 'Notificaciones'), value: stats.notifications_sent, color: 'text-purple-600' },
          ].map(s => (
            <Card key={s.label}><CardContent className="p-3 text-center">
              <p className={`font-heading font-bold text-xl ${s.color}`}>{s.value}</p>
              <p className="text-[10px] text-slate-400 uppercase">{s.label}</p>
            </CardContent></Card>
          ))}
        </div>
      )}

      <Tabs defaultValue="settings" className="space-y-4">
        <TabsList className="bg-slate-100 p-1 rounded-xl">
          <TabsTrigger value="settings" className="rounded-lg gap-1.5" data-testid="chatbot-tab-settings"><Settings className="w-4 h-4" />{tt('Settings', 'Configuracion')}</TabsTrigger>
          <TabsTrigger value="reminders" className="rounded-lg gap-1.5" data-testid="chatbot-tab-reminders"><Clock className="w-4 h-4" />{tt('Reminders', 'Recordatorios')}</TabsTrigger>
          <TabsTrigger value="notifications" className="rounded-lg gap-1.5" data-testid="chatbot-tab-notifications"><Bell className="w-4 h-4" />{tt('Notifications', 'Notificaciones')}</TabsTrigger>
          <TabsTrigger value="templates" className="rounded-lg gap-1.5" data-testid="chatbot-tab-templates"><FileText className="w-4 h-4" />{tt('Templates', 'Plantillas')}</TabsTrigger>
          <TabsTrigger value="simulator" className="rounded-lg gap-1.5" data-testid="chatbot-tab-simulator"><Play className="w-4 h-4" />{tt('Simulator', 'Simulador')}</TabsTrigger>
          <TabsTrigger value="log" className="rounded-lg gap-1.5" data-testid="chatbot-tab-log"><MessageSquare className="w-4 h-4" />{tt('Message Log', 'Registro')}</TabsTrigger>
        </TabsList>

        {/* ===== SETTINGS TAB ===== */}
        <TabsContent value="settings">
          <Card><CardContent className="p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-sm">{tt('Bot Enabled', 'Bot Habilitado')}</p>
                <p className="text-xs text-slate-400">{tt('Master switch for all chatbot features', 'Interruptor principal para todas las funciones del chatbot')}</p>
              </div>
              <button onClick={() => updateConfig({ enabled: !config.enabled })}
                className={`w-12 h-6 rounded-full transition-colors ${config.enabled ? 'bg-green-500' : 'bg-slate-300'}`}
                data-testid="toggle-bot-enabled">
                <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${config.enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
              </button>
            </div>
            <div className="border-t pt-4">
              <p className="font-semibold text-sm mb-3">{tt('WhatsApp Connection', 'Conexion WhatsApp')}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">{tt('Phone Number ID', 'ID Numero de Telefono')}</Label>
                  <Input placeholder="e.g., 1234567890" defaultValue={config.whatsapp_phone_id} className="mt-1"
                    data-testid="whatsapp-phone-id"
                    onBlur={e => { if (e.target.value !== config.whatsapp_phone_id) updateConfig({ whatsapp_phone_id: e.target.value }); }} />
                </div>
                <div>
                  <Label className="text-xs">{tt('Access Token', 'Token de Acceso')}</Label>
                  <Input placeholder="EAAxxxxxxx..." type="password" className="mt-1"
                    data-testid="whatsapp-token"
                    onBlur={e => { if (e.target.value) updateConfig({ whatsapp_token: e.target.value }); }} />
                </div>
              </div>
              <div className="mt-3">
                <Label className="text-xs">{tt('Verify Token', 'Token de Verificacion')}</Label>
                <Input defaultValue={config.verify_token} className="mt-1 w-64"
                  data-testid="verify-token"
                  onBlur={e => { if (e.target.value !== config.verify_token) updateConfig({ verify_token: e.target.value }); }} />
              </div>
              <div className="mt-3 bg-slate-50 rounded-lg p-3 text-xs text-slate-500">
                <p className="font-semibold mb-1">{tt('Webhook URL for Meta:', 'URL de Webhook para Meta:')}</p>
                <code className="bg-white px-2 py-1 rounded border text-primary break-all">
                  {`${process.env.REACT_APP_BACKEND_URL}/api/chatbot/webhook`}
                </code>
              </div>
            </div>
          </CardContent></Card>
        </TabsContent>

        {/* ===== REMINDERS TAB ===== */}
        <TabsContent value="reminders">
          <Card><CardContent className="p-5 space-y-4">
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="font-semibold text-sm">{tt('Automated Reminders', 'Recordatorios Automaticos')}</p>
                <p className="text-xs text-slate-400">{tt('Send reminders to users with pending actions', 'Enviar recordatorios a usuarios con acciones pendientes')}</p>
              </div>
              <button onClick={() => updateConfig({ reminders: { enabled: !reminders.enabled } })}
                className={`w-12 h-6 rounded-full transition-colors ${reminders.enabled ? 'bg-green-500' : 'bg-slate-300'}`}
                data-testid="toggle-reminders">
                <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${reminders.enabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
              </button>
            </div>
            {[
              { key: 'document_upload_interval_days', label: tt('Document Upload Reminder', 'Recordatorio Subir Documentos'), desc: tt('Days between reminders for pending document uploads', 'Dias entre recordatorios para documentos pendientes') },
              { key: 'poa_upload_interval_days', label: tt('POA Reminder', 'Recordatorio Poder'), desc: tt('Days between reminders for pending POA', 'Dias entre recordatorios para poder pendiente') },
              { key: 'offer_response_interval_days', label: tt('Offer Response Reminder', 'Recordatorio Respuesta Oferta'), desc: tt('Days between reminders for pending offer response', 'Dias entre recordatorios para respuesta de oferta') },
              { key: 'payment_proof_interval_days', label: tt('Payment Proof Reminder', 'Recordatorio Comprobante Pago'), desc: tt('Days between reminders for payment proof', 'Dias entre recordatorios para comprobante de pago') },
            ].map(item => (
              <div key={item.key} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid={`reminder-${item.key}`}>
                <div>
                  <p className="font-medium text-sm">{item.label}</p>
                  <p className="text-xs text-slate-400">{item.desc}</p>
                </div>
                <div className="flex items-center gap-2">
                  <Input type="number" min="1" max="30" className="w-16 h-8 text-center text-sm"
                    defaultValue={reminders[item.key] || 2}
                    data-testid={`interval-${item.key}`}
                    onBlur={e => updateConfig({ reminders: { [item.key]: parseInt(e.target.value) || 2 } })} />
                  <span className="text-xs text-slate-400">{tt('days', 'dias')}</span>
                </div>
              </div>
            ))}
            <Button onClick={triggerReminders} className="mt-3 bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C] gap-1" data-testid="trigger-reminders-btn">
              <Send className="w-3.5 h-3.5" />{tt('Send Reminders Now', 'Enviar Recordatorios Ahora')}
            </Button>
          </CardContent></Card>
        </TabsContent>

        {/* ===== NOTIFICATIONS TAB ===== */}
        <TabsContent value="notifications">
          <Card><CardContent className="p-5 space-y-3">
            <p className="font-semibold text-sm mb-2">{tt('Smart Notifications', 'Notificaciones Inteligentes')}</p>
            {[
              { key: 'offer_response', label: tt('Offer Response', 'Respuesta de Oferta'), desc: tt('Notify buyer when agent responds to offer', 'Notificar al comprador cuando el agente responde la oferta') },
              { key: 'document_uploaded', label: tt('Document Uploaded', 'Documento Subido'), desc: tt('Notify participants when a document is uploaded', 'Notificar participantes cuando se sube un documento') },
              { key: 'stage_change', label: tt('Stage Change', 'Cambio de Etapa'), desc: tt('Notify on transaction stage changes', 'Notificar cambios de etapa de transaccion') },
              { key: 'lawyer_assigned', label: tt('Lawyer Assigned', 'Abogado Asignado'), desc: tt('Notify lawyer when assigned to a transaction', 'Notificar al abogado al ser asignado a una transaccion') },
              { key: 'verification_update', label: tt('Verification Update', 'Actualizacion Verificacion'), desc: tt('Notify on verification approval/rejection', 'Notificar aprobacion/rechazo de verificacion') },
              { key: 'property_alerts', label: tt('Property Alerts', 'Alertas de Propiedades'), desc: tt('Send new property alerts to subscribed users', 'Enviar alertas de nuevas propiedades a usuarios suscritos') },
            ].map(item => (
              <div key={item.key} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid={`notif-${item.key}`}>
                <div>
                  <p className="font-medium text-sm">{item.label}</p>
                  <p className="text-xs text-slate-400">{item.desc}</p>
                </div>
                <button onClick={() => updateConfig({ notifications: { [item.key]: !notifications[item.key] } })}
                  className={`w-12 h-6 rounded-full transition-colors ${notifications[item.key] ? 'bg-green-500' : 'bg-slate-300'}`}
                  data-testid={`toggle-${item.key}`}>
                  <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${notifications[item.key] ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </button>
              </div>
            ))}
          </CardContent></Card>
        </TabsContent>

        {/* ===== TEMPLATES TAB ===== */}
        <TabsContent value="templates">
          <Card><CardContent className="p-5">
            <p className="font-semibold text-sm mb-3">{tt('Message Templates', 'Plantillas de Mensajes')}</p>
            <p className="text-xs text-slate-400 mb-4">{tt('Edit bilingual templates for all bot messages. Variables: {property}, {stage}, {status}, {amount}, {user_name}, {doc_type}, {buyer_name}, {title}, {city}, {price}', 'Editar plantillas bilingues. Variables: {property}, {stage}, {status}, {amount}, {user_name}, {doc_type}, {buyer_name}, {title}, {city}, {price}')}</p>
            <div className="space-y-2">
              {Object.entries(templates).map(([key, val]) => (
                <div key={key} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg" data-testid={`template-${key}`}>
                  <div className="flex-1 min-w-0 mr-3">
                    <p className="font-medium text-sm capitalize">{key.replace(/_/g, ' ')}</p>
                    <p className="text-xs text-slate-400 truncate">{val?.en?.substring(0, 60)}...</p>
                  </div>
                  <Dialog open={editingTemplate === key} onOpenChange={(open) => {
                    if (open) { setEditingTemplate(key); setTemplateEn(val?.en || ''); setTemplateEs(val?.es || ''); }
                    else setEditingTemplate(null);
                  }}>
                    <DialogTrigger asChild>
                      <Button size="sm" variant="outline" className="text-xs" data-testid={`edit-template-${key}`}>{tt('Edit', 'Editar')}</Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg" aria-describedby="tmpl-desc">
                      <DialogHeader><DialogTitle>{tt('Edit Template', 'Editar Plantilla')}: {key.replace(/_/g, ' ')}</DialogTitle></DialogHeader>
                      <p id="tmpl-desc" className="text-xs text-slate-400">{tt('Edit both English and Spanish versions.', 'Editar ambas versiones en ingles y espanol.')}</p>
                      <div className="space-y-3 mt-2">
                        <div><Label className="text-xs">English</Label><Textarea value={templateEn} onChange={e => setTemplateEn(e.target.value)} rows={3} className="mt-1" /></div>
                        <div><Label className="text-xs">Espanol</Label><Textarea value={templateEs} onChange={e => setTemplateEs(e.target.value)} rows={3} className="mt-1" /></div>
                        <Button onClick={saveTemplate} className="w-full bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C]">{tt('Save', 'Guardar')}</Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              ))}
            </div>
          </CardContent></Card>
        </TabsContent>

        {/* ===== SIMULATOR TAB ===== */}
        <TabsContent value="simulator">
          <Card><CardContent className="p-5">
            <p className="font-semibold text-sm mb-1">{tt('Chat Simulator', 'Simulador de Chat')}</p>
            <p className="text-xs text-slate-400 mb-4">{tt('Test the chatbot without WhatsApp. Enter a phone number (must match a registered user) and a message.', 'Prueba el chatbot sin WhatsApp. Ingresa un numero (debe coincidir con un usuario registrado) y un mensaje.')}</p>
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">{tt('Phone', 'Telefono')}</Label>
                  <Input value={simPhone} onChange={e => setSimPhone(e.target.value)} placeholder="+1-555-123-4567" className="mt-1" data-testid="sim-phone" />
                </div>
                <div className="col-span-2">
                  <Label className="text-xs">{tt('Message', 'Mensaje')}</Label>
                  <div className="flex gap-2 mt-1">
                    <Input value={simText} onChange={e => setSimText(e.target.value)} placeholder="status" className="flex-1" data-testid="sim-text"
                      onKeyDown={e => { if (e.key === 'Enter') simulate(); }} />
                    <Button onClick={simulate} disabled={simulating} className="bg-[#D4AF37] hover:bg-[#C8A028] text-[#0B192C] gap-1" data-testid="sim-send">
                      {simulating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                    </Button>
                  </div>
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                {['help', 'status', 'properties Caracas', 'alerts on', 'language es'].map(cmd => (
                  <button key={cmd} onClick={() => { setSimText(cmd); }}
                    className="text-xs px-2.5 py-1 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors">
                    {cmd}
                  </button>
                ))}
              </div>
              {simResponse && (
                <div className="bg-green-50 border border-green-200 rounded-xl p-4 mt-2" data-testid="sim-response">
                  <p className="text-[10px] text-green-500 font-semibold uppercase mb-1">{tt('Bot Response', 'Respuesta del Bot')}</p>
                  <p className="text-sm text-green-800 whitespace-pre-wrap">{simResponse}</p>
                </div>
              )}
            </div>
          </CardContent></Card>
        </TabsContent>

        {/* ===== MESSAGE LOG TAB ===== */}
        <TabsContent value="log">
          <Card><CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="font-semibold text-sm">{tt('Recent Messages', 'Mensajes Recientes')}</p>
              <Badge variant="outline" className="text-xs">{messages.length} {tt('messages', 'mensajes')}</Badge>
            </div>
            {messages.length > 0 ? (
              <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                {messages.map(msg => (
                  <div key={msg.id} className={`p-3 rounded-lg text-sm ${msg.direction === 'incoming' ? 'bg-blue-50 border-l-3 border-blue-400' : 'bg-green-50 border-l-3 border-green-400'}`}
                    data-testid={`msg-${msg.id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <Badge className={`text-[9px] ${msg.direction === 'incoming' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                          {msg.direction === 'incoming' ? tt('IN', 'ENT') : tt('OUT', 'SAL')}
                        </Badge>
                        {msg.type !== 'chat' && <Badge className="text-[9px] bg-purple-100 text-purple-700">{msg.type}</Badge>}
                        <span className="text-xs text-slate-400">{msg.phone}</span>
                      </div>
                      <span className="text-[10px] text-slate-400">{new Date(msg.created_at).toLocaleString()}</span>
                    </div>
                    <p className="text-slate-700 text-xs whitespace-pre-wrap">{msg.text}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <MessageSquare className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-400">{tt('No messages yet. Use the simulator to test!', 'No hay mensajes. Usa el simulador para probar!')}</p>
              </div>
            )}
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
